// The OEM discount rule editor. THE FIRST REAL WRITE ON THE /pricing SCREEN.
//
// ⚠️ ADMIN ONLY, AND THAT IS COMMERCIAL RATHER THAN TECHNICAL. A discount rule
// flows straight into every quote priced afterwards, so Sales creating one
// would let the person negotiating a deal also set the floor for it. The
// BACKEND enforces it -- save_discount_rule carries
// @require_access(GROUP_ADMIN, "admin") plus @state_changing -- and this
// component only avoids OFFERING an action that would come back 403.
//
// ⚠️ A NON-ADMIN SEES THE RULE READ-ONLY, NOT HIDDEN. Anyone who quotes needs
// to know which rate applies; that is why the read endpoint sits at viewer.
// The fields are `readOnly` rather than `disabled`, for the same reason
// Settings > Company is: a disabled input is skipped by keyboard navigation
// and unannounced by screen readers, so the value would be unreachable rather
// than merely uneditable.
//
// ⚠️ THERE IS NO DELETE, DELIBERATELY. A rate is superseded by adding its
// replacement and ARCHIVING this one, which is what the model's partial unique
// index expects and what keeps the record of what a line was priced under.
// "Archive" is the destructive-looking control here and it is not destructive.
//
// ⚠️ "CREATE AND ADD ANOTHER" IS ENTRY ERGONOMICS AND NOTHING ELSE. It posts
// the SAME payload through the SAME save(), once, and then reseeds this form
// for the next rule instead of handing the saved row back as the selection. It
// does not batch, does not defer, does not retry, and does not touch the
// duplicate-pairing refusal -- one press is still exactly one POST, and the
// model still refuses a second active rule for a pairing with its own message.
// A "save several at once" control would have to decide what a half-applied
// batch means, and that is a different feature with different semantics.

import { useEffect, useMemo, useRef, useState } from "react";
import type { OemDiscountRule, CustomerPartner } from "@/types";
import type { BrandOption } from "@/services/api";
import { saveDiscountRule, searchCustomers } from "@/services/api";
import { useCanEditSettings } from "@/auth/AuthContext";
import { DetailPanel } from "@/components/DetailPanel";
import { ReadOnlySave, READ_ONLY_NOTE } from "@/components/ReadOnlySave";
import {
  colors, semanticColors, radius, spacing, heights, typography,
} from "@/styles/tokens";

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};
const INPUT: React.CSSProperties = {
  width: "100%", padding: `${spacing[6]}px ${spacing[10]}px`, height: heights.input,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none", boxSizing: "border-box",
};
const READONLY_INPUT: React.CSSProperties = {
  ...INPUT,
  backgroundColor: semanticColors.panelBg,
  color: semanticColors.textSecondary,
  cursor: "default",
};

/** What a non-admin is told. Names the role that CAN, so somebody who cannot
 *  edit knows who to ask rather than only that they are blocked -- same
 *  wording shape as Settings > Company's note. */
const NON_ADMIN_NOTE =
  `${READ_ONLY_NOTE} for your role — an Anixify Admin can change discount rules.`;

/** Pick ONE partner, by id.
 *
 *  ⚠️ NOT components/CustomerPicker.tsx, and the difference is not stylistic.
 *  That component picks the CUSTOMER OF A QUOTE: it resolves shipping, invoice
 *  and contact children, offers an inline create, and hands back a whole
 *  QuoteCustomer. A discount rule needs one thing -- a res.partner id -- and
 *  reusing the quote picker would drag an address hierarchy and a create path
 *  into a screen that has no use for either, and would let an Admin create a
 *  customer as a side effect of writing a rate.
 *
 *  ⚠️ THE ID IS WHAT IS STORED. The name is display only. searchCustomers
 *  already applies the entity filter (is_company, top-level, not our own
 *  company), so this offers exactly what the quote screen offers. */
function PartnerSearch({
  value, onPick,
}: {
  value: string;
  onPick: (partner: CustomerPartner) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CustomerPartner[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  // Last-response-wins. Two keystrokes in flight can land out of order, and
  // the older answer would overwrite the newer -- the same token the catalog
  // search uses, for the same reason.
  const token = useRef(0);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const mine = ++token.current;
    const timer = setTimeout(async () => {
      const result = await searchCustomers(query, 10);
      if (mine !== token.current) return;
      if (!result.ok) {
        setResults([]);
        setError(result.error ?? "Could not search customers.");
        return;
      }
      setError("");
      setResults(result.customers ?? []);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div style={{ position: "relative" }}>
      <input
        data-testid="discount-partner"
        type="text"
        value={open ? query : value}
        placeholder={value ? value : "Search customers…"}
        onFocus={() => { setOpen(true); setQuery(""); }}
        onChange={(e) => setQuery(e.target.value)}
        style={INPUT}
      />
      {open && error && (
        <div data-testid="discount-partner-error"
             style={{ marginTop: spacing[4], fontSize: typography.fontSize.xs, color: semanticColors.destructive }}>
          {error}
        </div>
      )}
      {open && results.length > 0 && (
        <div
          data-testid="discount-partner-results"
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 2,
            marginTop: spacing[4], maxHeight: 200, overflowY: "auto",
            backgroundColor: semanticColors.surface,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          }}
        >
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`discount-partner-option-${c.id}`}
              onClick={() => { onPick(c); setOpen(false); setQuery(""); setResults([]); }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: `${spacing[6]}px ${spacing[10]}px`,
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: typography.fontSize.sm, color: semanticColors.textPrimary,
              }}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Today, on the LOCAL calendar, as the yyyy-mm-dd an <input type="date">
 *  wants.
 *
 *  ⚠️ NOT `new Date().toISOString().slice(0, 10)`, and the difference bites in
 *  exactly this office. That formats the UTC date, so an Admin in IST (UTC+5:30)
 *  entering a rule before 05:30 would stamp it YESTERDAY -- a wrong audit date,
 *  silently, on the one field nobody re-reads because it does not affect
 *  pricing. */
function todayIso(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/** What "Agreed On" starts at.
 *
 *  ⚠️ TODAY ON A NEW RULE ONLY. Editing an existing one shows what is STORED,
 *  including when that is empty -- a blank stored date is a fact about the
 *  record ("nobody recorded when this was agreed"), and defaulting it to today
 *  on open would let a plain Save rewrite that absence into a date nobody
 *  agreed anything on. The field is audit information; inventing it is the one
 *  way it could mislead. */
function seedEffectiveFrom(rule: OemDiscountRule | null): string {
  return rule ? (rule.effectiveFrom ?? "") : todayIso();
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={LABEL}>{label}</span>
      {children}
    </div>
  );
}

export function OemDiscountRulePanel({
  rule, brands, onClose, onSaved,
}: {
  /** null = creating a new rule. */
  rule: OemDiscountRule | null;
  brands: BrandOption[];
  onClose: () => void;
  /** The parent reloads the list. Passed the saved rule so it can select it.
   *
   *  ⚠️ `keepCreating` tells the parent NOT to select the saved row -- the
   *  panel has already reseeded itself for the next rule and selecting would
   *  flip it into edit mode over the top of a half-typed one. */
  onSaved: (saved: OemDiscountRule, keepCreating?: boolean) => void;
}) {
  const canEdit = useCanEditSettings();
  const creating = rule === null;
  /** Focused after "Create and add another" -- the customer is retained, so
   *  OEM is the first field that actually needs an answer. Without this the
   *  next rule starts with focus on a button and the Admin reaches for the
   *  mouse, which is most of what the control was meant to save. */
  const brandRef = useRef<HTMLSelectElement>(null);

  const [partnerId, setPartnerId] = useState<number | undefined>(rule?.partnerId);
  const [partnerName, setPartnerName] = useState<string>(rule?.partnerName ?? "");
  const [brandId, setBrandId] = useState<number | undefined>(rule?.brandId);
  const [discountPct, setDiscountPct] = useState<string>(
    rule ? String(rule.discountPct) : "",
  );
  const [effectiveFrom, setEffectiveFrom] = useState<string>(seedEffectiveFrom(rule));
  const [notes, setNotes] = useState<string>(rule?.notes ?? "");
  const [active, setActive] = useState<boolean>(rule?.active ?? true);

  const [status, setStatus] = useState<"idle" | "saving" | "error">("idle");
  const [error, setError] = useState("");

  // Reseed when the selected rule changes -- the parent keeps this component
  // mounted across selections.
  useEffect(() => {
    setPartnerId(rule?.partnerId);
    setPartnerName(rule?.partnerName ?? "");
    setBrandId(rule?.brandId);
    setDiscountPct(rule ? String(rule.discountPct) : "");
    setEffectiveFrom(seedEffectiveFrom(rule));
    setNotes(rule?.notes ?? "");
    setActive(rule?.active ?? true);
    setStatus("idle");
    setError("");
  }, [rule]);

  // ⚠️ THE PERCENTAGE IS VALIDATED HERE ONLY TO AVOID A POINTLESS ROUND TRIP.
  // The backend's CHECK constraint is the actual rule, and it is the one that
  // holds against anything that does not come through this form.
  const pctNumber = discountPct.trim() === "" ? undefined : Number(discountPct);
  const pctValid =
    pctNumber !== undefined && Number.isFinite(pctNumber) && pctNumber >= 0 && pctNumber <= 100;

  const canSubmit = useMemo(
    () => canEdit && partnerId !== undefined && brandId !== undefined && pctValid,
    [canEdit, partnerId, brandId, pctValid],
  );

  async function save(overrides: { active?: boolean; keepCreating?: boolean } = {}) {
    setStatus("saving");
    setError("");

    const result = await saveDiscountRule({
      ...(rule ? { ruleId: Number(rule.id) } : {}),
      partnerId,
      brandId,
      discountPct: pctNumber,
      // "" clears the stored date. Audit only either way.
      effectiveFrom,
      notes,
      active: overrides.active ?? active,
    });

    if (!result.ok || !result.rule) {
      // ⚠️ NEVER "Saved" WHEN IT WAS NOT, and the local edits stay on screen so
      // nothing typed is lost to a failed request. The duplicate-pairing
      // refusal arrives here as the MODEL's message, which names the customer,
      // the brand, the existing rate and the remedy.
      setError(result.error ?? "Backend reported an unknown failure.");
      setStatus("error");
      return;
    }
    setStatus("idle");

    if (overrides.keepCreating) {
      // ⚠️ THE CUSTOMER SURVIVES; EVERYTHING THAT MAKES IT A DIFFERENT RULE
      // DOES NOT. Batches are entered customer-first -- four Kestrel rates, then
      // two Calderon ones -- so retaining the partner is the whole saving.
      // Retaining the OEM or the percentage would be the opposite of a saving:
      // the pairing is unique per (customer, brand), so a kept brand can only
      // produce the duplicate refusal, and a kept percentage is a rate from
      // the last rule sitting pre-filled in a field whose whole job is to be
      // correct. Notes go too -- an agreement reference belongs to one rule.
      setBrandId(undefined);
      setDiscountPct("");
      setNotes("");
      setEffectiveFrom(todayIso());
      // The parent leaves `rule` null, so the reseed effect does not fire and
      // does not undo the four lines above.
      onSaved(result.rule, true);
      brandRef.current?.focus();
      return;
    }

    onSaved(result.rule);
  }

  const footer = (
    // flexWrap because create mode carries a third button and the panel is a
    // fixed 420px -- three labels plus gaps sit within a few pixels of the
    // footer's usable width, and a font that renders a little wider would
    // otherwise push "Create rule" out of the panel rather than onto a second
    // row. Nothing wraps at the measured widths; this is the guard, not the
    // layout.
    <div className="flex items-center" style={{ gap: spacing[8], width: "100%", flexWrap: "wrap" }}>
      <button
        data-testid="discount-panel-cancel"
        className="btn-secondary"
        onClick={onClose}
        style={{
          height: heights.controlLg, padding: `0 ${spacing[14]}px`,
          backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          fontSize: typography.fontSize.base, cursor: "pointer",
        }}
      >
        Close
      </button>

      {!canEdit && <ReadOnlySave testId="discount-panel-save" />}

      {canEdit && !creating && (
        // ⚠️ NOT A DELETE. Archiving is how a rate is superseded and it keeps
        // the record of what a line was priced under. Styled as a secondary
        // control for that reason -- it is an ordinary act, not a destructive
        // one.
        <button
          data-testid="discount-panel-archive"
          className="btn-secondary"
          disabled={status === "saving"}
          onClick={() => { setActive(!active); void save({ active: !active }); }}
          style={{
            height: heights.controlLg, padding: `0 ${spacing[14]}px`,
            backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            fontSize: typography.fontSize.base, cursor: "pointer",
            opacity: status === "saving" ? 0.6 : 1,
          }}
        >
          {active ? "Archive" : "Reactivate"}
        </button>
      )}

      {canEdit && creating && (
        // ⚠️ ONLY WHEN CREATING. On an existing rule there is no "another" to
        // add -- the next act is Save or Archive -- and offering it there would
        // read as "duplicate this rule", which the unique index refuses anyway.
        <button
          data-testid="discount-panel-save-and-new"
          className="btn-secondary"
          disabled={!canSubmit || status === "saving"}
          onClick={() => void save({ keepCreating: true })}
          style={{
            height: heights.controlLg, padding: `0 ${spacing[14]}px`,
            backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            fontSize: typography.fontSize.base, whiteSpace: "nowrap",
            cursor: canSubmit ? "pointer" : "not-allowed",
            opacity: canSubmit && status !== "saving" ? 1 : 0.6,
          }}
        >
          Create and add another
        </button>
      )}

      {canEdit && (
        <button
          data-testid="discount-panel-save"
          className="btn-primary"
          disabled={!canSubmit || status === "saving"}
          onClick={() => void save()}
          style={{
            height: heights.controlLg, padding: `0 ${spacing[14]}px`,
            backgroundColor: colors.indigo600, color: semanticColors.onAccent,
            border: "none", borderRadius: radius.base,
            fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
            cursor: canSubmit ? "pointer" : "not-allowed",
            opacity: canSubmit && status !== "saving" ? 1 : 0.6,
          }}
        >
          {status === "saving" ? "Saving…" : creating ? "Create rule" : "Save"}
        </button>
      )}
    </div>
  );

  return (
    <DetailPanel
      testIdPrefix="discount"
      title={creating ? "New discount rule" : (rule?.partnerName ?? "Discount rule")}
      subtitle={
        creating
          ? "A standing rate this customer negotiated with an OEM"
          : `${rule?.brandName ?? "—"} · ${rule?.discountPct}%`
      }
      onClose={onClose}
      footer={footer}
    >
      {!canEdit && (
        <div
          data-testid="discount-panel-readonly-note"
          style={{
            marginBottom: spacing[14], fontSize: typography.fontSize.sm,
            color: semanticColors.textMuted,
          }}
        >
          {NON_ADMIN_NOTE}
        </div>
      )}

      {/* ⚠️ THE STANDING-RATES-ONLY STATEMENT, ON THE FORM ITSELF. Without it
          the obvious thing to do with a competitive bid is to record it here
          as a rule -- which the owner said must never happen, because it is a
          negotiation that is different every time rather than a rate. Naming
          the alternative is what makes the refusal actionable. */}
      <div
        data-testid="discount-panel-standing-only"
        style={{
          marginBottom: spacing[14], padding: spacing[12],
          backgroundColor: colors.indigo50, border: `1px solid ${colors.indigo100}`,
          borderRadius: radius.base,
          fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
        }}
      >
        Standing negotiated rates only — the rate this customer has registered
        with this OEM. A competitive bid is priced per deal: enter the net cost
        the OEM quoted directly on the quote line instead.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: spacing[14] }}>
        <Field label="Customer">
          {canEdit ? (
            <PartnerSearch
              value={partnerName}
              onPick={(c: CustomerPartner) => {
                setPartnerId(c.id);
                setPartnerName(c.name);
              }}
            />
          ) : (
            <input
              data-testid="discount-partner-readonly"
              value={partnerName}
              readOnly
              aria-readonly="true"
              style={READONLY_INPUT}
            />
          )}
        </Field>

        <Field label="OEM">
          <select
            data-testid="discount-brand"
            ref={brandRef}
            value={brandId ?? ""}
            disabled={!canEdit}
            onChange={(e) => setBrandId(e.target.value ? Number(e.target.value) : undefined)}
            style={canEdit ? INPUT : READONLY_INPUT}
          >
            <option value="">Choose an OEM…</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </Field>

        <Field label="OEM Discount %">
          <input
            data-testid="discount-pct"
            type="number"
            min={0}
            max={100}
            step="0.1"
            value={discountPct}
            readOnly={!canEdit}
            aria-readonly={!canEdit || undefined}
            onChange={(e) => setDiscountPct(e.target.value)}
            style={canEdit ? INPUT : READONLY_INPUT}
          />
          {/* ⚠️ SAYS WHOSE DISCOUNT IT IS. There is no customer-side discount
              at Anixify -- every percentage here reduces what Anixify PAYS,
              and it never reaches a customer document. */}
          <span style={{ marginTop: spacing[4], fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
            Off the OEM list price. Reduces what Anixify pays; never shown to
            the customer.
          </span>
          {canEdit && discountPct.trim() !== "" && !pctValid && (
            <span
              data-testid="discount-pct-error"
              style={{ marginTop: spacing[4], fontSize: typography.fontSize.xs, color: semanticColors.destructive }}
            >
              Must be between 0 and 100.
            </span>
          )}
        </Field>

        <Field label="Agreed On">
          <input
            data-testid="discount-effective-from"
            type="date"
            value={effectiveFrom}
            readOnly={!canEdit}
            aria-readonly={!canEdit || undefined}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            style={canEdit ? INPUT : READONLY_INPUT}
          />
          {/* ⚠️ THE LABEL AND THIS NOTE ARE LOAD-BEARING. It is NOT "Valid
              From". A rate applies until it is archived, not until a date
              passes -- if this gated matching, a rate would silently stop
              applying at midnight and lines that priced yesterday would refuse
              today, pointing at a rule visibly sitting in the table. */}
          <span style={{ marginTop: spacing[4], fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
            Audit information. It does not switch the rate on or off — a rate
            applies until it is archived.
          </span>
        </Field>

        <Field label="Notes">
          <input
            data-testid="discount-notes"
            type="text"
            value={notes}
            readOnly={!canEdit}
            aria-readonly={!canEdit || undefined}
            placeholder="Agreement reference, contact, anything worth recording"
            onChange={(e) => setNotes(e.target.value)}
            style={canEdit ? INPUT : READONLY_INPUT}
          />
        </Field>

        {status === "error" && (
          <div
            data-testid="discount-panel-error"
            style={{
              padding: spacing[12],
              border: `1px solid ${semanticColors.destructiveBorder}`,
              backgroundColor: semanticColors.destructiveBg,
              borderRadius: radius.base,
              fontSize: typography.fontSize.sm, color: semanticColors.destructive,
            }}
          >
            Not saved. {error}
          </div>
        )}
      </div>
    </DetailPanel>
  );
}
